import React, { useState, useEffect } from 'react';

import { Box } from '@atlaskit/primitives/compiled';
import { cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { ContentAppearanceType } from '@confluence/content-appearance/entry-points/contentAppearanceTypes';
import {
	useCommentsPanelState,
	useCommentsPanelActions,
	AddFooterCommentMode,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import {
	useObjectCommentDispatchContext,
	useObjectCommentContext,
} from '@confluence/comment-context/entry-points/ObjectCommentContext';

import { DraftCommentBox } from './DraftCommentBox';
import { ObjectAddCommentProfileWrapper } from './ObjectAddCommentProfileWrapper';

const styles = cssMap({
	stickyDraftViewPageWrapper: {
		position: 'sticky',
		bottom: token('space.0'),
		backgroundColor: token('elevation.surface'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderColor: token('color.border'),
		padding: token('space.200'),
		margin: '0 auto',
	},
	stickyDraftLivePageWrapper: {
		position: 'fixed',
		bottom: token('space.0'),
		backgroundColor: token('elevation.surface'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderColor: token('color.border'),
		padding: token('space.200'),
		margin: '0 auto',
		zIndex: 500,
	},
	hiddenStyles: {
		display: 'none',
	},
	visibleStyles: {
		display: 'block',
	},
});

export type StickyDraftCommentContainerProps = {
	pageWidthType?: string;
};

export const StickyDraftCommentContainer: React.FC<StickyDraftCommentContainerProps> = ({
	pageWidthType,
}) => {
	const [isCommentWrapperVisible, setIsCommentWrapperVisible] = useState(true);
	const { expanded, source } = useObjectCommentContext();
	const { expandCommentContainer, setWrapperWidth } = useObjectCommentDispatchContext();
	const [objectCommentWrapperDimensions, setObjectCommentWrapperDimensions] = useState<{
		width: number;
	} | null>(null);
	const { setAddFooterCommentMode } = useCommentsPanelActions();

	const pageMode = useGetPageMode();

	const isViewPage = pageMode === PageMode.VIEW;

	const { addFooterCommentAdf } = useCommentsPanelState();

	// Hide the sticky draft when the comment wrapper is visible so that when they scroll back up they don't have the full editor visible
	useEffect(() => {
		if (isCommentWrapperVisible && expanded) {
			expandCommentContainer();
		}
	}, [isCommentWrapperVisible, expandCommentContainer, expanded]);

	useEffect(() => {
		let intersectionObserver: IntersectionObserver | null = null;
		let resizeObserver: ResizeObserver | null = null;

		const commentWrapperSelector = '[data-testid="object-comment-wrapper"]';

		const calculateObjectCommentWrapperWidth = () => {
			// Find the object-comment-wrapper element and match its dimensions
			const objectCommentWrapper = document.querySelector(commentWrapperSelector);
			if (objectCommentWrapper) {
				const rect = objectCommentWrapper.getBoundingClientRect();
				setObjectCommentWrapperDimensions({
					width: rect.width,
				});
				setWrapperWidth(rect.width);
			}
		};

		const setupObservers = () => {
			const commentWrapper = document.querySelector(commentWrapperSelector);

			if (!commentWrapper) {
				setIsCommentWrapperVisible(false);
				return;
			}

			// Set up intersection observer for visibility tracking
			if (!intersectionObserver) {
				intersectionObserver = new IntersectionObserver(
					([entry]) => {
						// Check if the comment wrapper is visible in the viewport
						// We want to hide the sticky draft when the comment wrapper is visible
						setIsCommentWrapperVisible(entry.isIntersecting);
					},
					{
						// No rootMargin - use default intersection detection
						rootMargin: '0px',
					},
				);
				intersectionObserver.observe(commentWrapper);
			}

			// Set up resize observer for dimension tracking (only for live pages)
			if (!resizeObserver) {
				resizeObserver = new ResizeObserver(() => {
					calculateObjectCommentWrapperWidth();
				});
				resizeObserver.observe(commentWrapper);
			}

			// Calculate initial dimensions for live pages
			calculateObjectCommentWrapperWidth();
		};

		// Try to set up observers immediately
		setupObservers();

		// Set up a single MutationObserver to watch for when the comment wrapper gets added to the DOM
		// This happens when the wrapper is lazy loaded
		const mutationObserver = new MutationObserver(() => {
			const commentWrapper = document.querySelector('[data-testid="object-comment-wrapper"]');
			if (commentWrapper && (!intersectionObserver || (!isViewPage && !resizeObserver))) {
				setupObservers();
			}
		});

		mutationObserver.observe(document.body, {
			childList: true,
			subtree: true,
		});

		return () => {
			if (intersectionObserver) {
				intersectionObserver.disconnect();
			}
			if (resizeObserver) {
				resizeObserver.disconnect();
			}
			mutationObserver.disconnect();
		};
	}, [isViewPage, setWrapperWidth]);

	// Update global state based on footer visibility
	useEffect(() => {
		if (isCommentWrapperVisible) {
			// Footer is visible, so we're in FOOTER mode
			setAddFooterCommentMode(AddFooterCommentMode.FOOTER);
		} else {
			// Footer is not visible, so we're in STICKY_DRAFT mode
			setAddFooterCommentMode(AddFooterCommentMode.STICKY_DRAFT);
		}
	}, [isCommentWrapperVisible, setAddFooterCommentMode]);

	const getMaxWidth = () => {
		if (pageWidthType === undefined) {
			return '100%';
		}
		// pageWidthType is a string like '"default"'
		return JSON.parse(pageWidthType) === ContentAppearanceType.DEFAULT ? '1000px' : 'unset';
	};

	// Show the sticky draft if the comment wrapper is not visible and there is an ADF or the editor is expanded
	// This is to prevent the draft comment from disappearing if you delete all of the text
	const showStickyDraft =
		!isCommentWrapperVisible && addFooterCommentAdf && source !== AddFooterCommentMode.STICKY_DRAFT;

	// Use hidden / visible styles below to prevent unnecessary unmounting (which can have side effects, specifically for editor)
	return (
		<Box
			xcss={cx(
				isViewPage ? styles.stickyDraftViewPageWrapper : styles.stickyDraftLivePageWrapper,
				showStickyDraft ? styles.visibleStyles : styles.hiddenStyles,
			)}
			testId="sticky-draft-comment-box"
			style={{
				maxWidth: isViewPage ? getMaxWidth() : '100%',
				width:
					!isViewPage && objectCommentWrapperDimensions
						? `${objectCommentWrapperDimensions.width}px`
						: '100%',
			}}
			id="addCommentButton-draft"
		>
			<ObjectAddCommentProfileWrapper>
				<Box xcss={cx(!showStickyDraft ? styles.hiddenStyles : styles.visibleStyles)}>
					<DraftCommentBox
						expandPageCommentEditorHandler={() =>
							expandCommentContainer(AddFooterCommentMode.STICKY_DRAFT)
						}
					/>
				</Box>
			</ObjectAddCommentProfileWrapper>
		</Box>
	);
};
